import { FC, ReactNode } from 'react';

import { FormattedMessage } from 'dibs-react-intl';
import Circle from 'dibs-icons/exports/legacy/Circle';
import Checkmark from 'dibs-icons/exports/legacy/Checkmark';
import XMark from 'dibs-icons/exports/legacy/XMark';

import { validationErrorCodes } from '../authentication/validations/validatePassword';
import {
    MIN_PASSWORD_LENGTH,
    MIN_PASSWORD_NUMBERS_OR_SPECIAL_CHARACTERS,
    MIN_PASSWORD_LETTERS,
} from '../helpers/validators';

import styles from './PasswordRequirements.scss';

type Props = {
    errorCodes: Record<string, boolean> | null;
};

type RequirementsErrorType = {
    errorCodes: Record<string, boolean> | null;
    errorCodeItem: string;
};

const errorExists = ({ errorCodes, errorCodeItem }: RequirementsErrorType): boolean =>
    !!errorCodes?.[errorCodeItem];

const getIcon = ({ errorCodes, errorCodeItem }: RequirementsErrorType): ReactNode => {
    if (errorCodes === null) {
        return <Circle className={styles.circle} />;
    }

    if (errorExists({ errorCodes, errorCodeItem })) {
        return <XMark className={styles.xMark} />;
    }

    return <Checkmark className={styles.checkmark} />;
};

const getIconContainer = (requirementsError: RequirementsErrorType): ReactNode => (
    <div className={styles.iconContainer}>{getIcon(requirementsError)}</div>
);

const getRequirementClassName = ({
    errorCodes,
    errorCodeItem,
}: RequirementsErrorType): string | undefined =>
    errorExists({ errorCodes, errorCodeItem }) ? styles.notCompletedRequirement : undefined;

const getRequirementDataTn = ({ errorCodes, errorCodeItem }: RequirementsErrorType): string => {
    let requirementStatus = 'valid';

    if (errorCodes === null) {
        requirementStatus = 'unvalidated';
    } else if (errorExists({ errorCodes, errorCodeItem })) {
        requirementStatus = 'error';
    }

    return `password-requirement-${errorCodeItem}-${requirementStatus}`;
};

const PasswordRequirements: FC<Props> = ({ errorCodes }) => (
    <div className={styles.requirementsGrid}>
        <div className={styles.header}>
            <FormattedMessage
                id="dbl.authmodal.PasswordRequirements.header"
                defaultMessage="Password Requirements:"
            />
        </div>
        {getIconContainer({ errorCodes, errorCodeItem: validationErrorCodes.TOO_SHORT })}
        <div
            className={getRequirementClassName({
                errorCodes,
                errorCodeItem: validationErrorCodes.TOO_SHORT,
            })}
            data-tn={getRequirementDataTn({
                errorCodes,
                errorCodeItem: validationErrorCodes.TOO_SHORT,
            })}
        >
            <FormattedMessage
                id="dbl.authmodal.PasswordRequirements.tooShort"
                defaultMessage="Minimum of {amount} characters"
                values={{ amount: MIN_PASSWORD_LENGTH }}
            />
        </div>
        {getIconContainer({
            errorCodes,
            errorCodeItem: validationErrorCodes.NUMBER_OR_SPECIAL_CHARACTER,
        })}
        <div
            className={getRequirementClassName({
                errorCodes,
                errorCodeItem: validationErrorCodes.NUMBER_OR_SPECIAL_CHARACTER,
            })}
            data-tn={getRequirementDataTn({
                errorCodes,
                errorCodeItem: validationErrorCodes.NUMBER_OR_SPECIAL_CHARACTER,
            })}
        >
            <FormattedMessage
                id="dbl.authmodal.PasswordRequirements.numberOrSpecialCharacter"
                defaultMessage="At least {amount} number or special character"
                values={{ amount: MIN_PASSWORD_NUMBERS_OR_SPECIAL_CHARACTERS }}
            />
        </div>
        {getIconContainer({ errorCodes, errorCodeItem: validationErrorCodes.LETTER })}
        <div
            className={getRequirementClassName({
                errorCodes,
                errorCodeItem: validationErrorCodes.LETTER,
            })}
            data-tn={getRequirementDataTn({
                errorCodes,
                errorCodeItem: validationErrorCodes.LETTER,
            })}
        >
            <FormattedMessage
                id="dbl.authmodal.PasswordRequirements.letter"
                defaultMessage="At least {amount} letter"
                values={{ amount: MIN_PASSWORD_LETTERS }}
            />
        </div>
    </div>
);

export default PasswordRequirements;
